import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { subscribeToGroupUpdates } from '@/lib/firebase/queries/groups';
import { subscribeToUnreadCounts } from '@/lib/firebase/queries/groupReads';
import { selectMyGroupIds } from '@/lib/groups/myGroupIds';
import { totalUnread } from '@/lib/groups/groupUnread';
import type { Group } from '@/types/support';

/**
 * Unread group chat messages across the member's groups in `organizationId`
 * plus their personal groups (all their groups when undefined), for the
 * navigation badge (namat#36 P2). Same scope as the groups list
 * (SupportGroups → GroupList), so the badge counts exactly the listed groups.
 * Pass enabled=false to skip all reads, e.g. without the supportGroups section.
 */
export function useGroupsUnreadTotal(
  userId: string | undefined,
  organizationId: string | undefined,
  enabled = true,
): number {
  const [groups, setGroups] = useState<Group[]>([]);
  const [memberGroupIds, setMemberGroupIds] = useState<string[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!enabled || !userId) return;
    return subscribeToGroupUpdates(setGroups, undefined, organizationId, { includePersonal: true });
  }, [enabled, userId, organizationId]);

  useEffect(() => {
    if (!enabled || !userId) return;
    return onSnapshot(
      query(collection(db, 'groupMembers'), where('userId', '==', userId)),
      (snap) => setMemberGroupIds(snap.docs.map((d) => d.data().groupId as string).filter(Boolean)),
      (error) => console.error('Error loading my group memberships:', error),
    );
  }, [enabled, userId]);

  const groupIdsKey = useMemo(
    () => selectMyGroupIds(groups, memberGroupIds).sort().join(','),
    [groups, memberGroupIds],
  );

  useEffect(() => {
    if (!enabled || !userId || !groupIdsKey) {
      setCounts({});
      return;
    }
    return subscribeToUnreadCounts(groupIdsKey.split(','), userId, setCounts);
  }, [enabled, userId, groupIdsKey]);

  return groupIdsKey ? totalUnread(counts, groupIdsKey.split(',')) : 0;
}
